import {
    useMutation,
    useQueryClient,
    UseMutationResult,
} from '@tanstack/react-query'
import { addAttribute } from '../../../../services/dtServices'
import { IAttribute } from '../../interfaces'

export const useCreateAttribute = (
    orgId: string,
    entityId: string,
): UseMutationResult<
    Awaited<ReturnType<typeof addAttribute>>,
    Error,
    IAttribute
> => {
    const queryClient = useQueryClient()
    const queryKey = ['attributes', entityId, orgId]
    return useMutation({
        mutationFn: (attribute: IAttribute) => {
            return addAttribute(orgId, entityId, {
                ...attribute,
                isSearchable: true,
            })
        },
        /**
         * Cancel any in-flight attribute fetch so a slow, already-running GET
         * can't resolve later and clobber the cache. We intentionally do NOT
         * optimistically add a row: the create response carries no server id,
         * so an optimistic row could only use a fabricated id (which surfaced
         * as a "duplicate row with unknown ID" phantom, UBQ-5435). MRT's
         * `isSaving` state covers the in-progress feedback; the real row lands
         * via the onSettled refetch. With no optimistic write there is nothing
         * to roll back, so no snapshot/onError is needed here.
         */
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey })
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    })
}
